import type { DeveloperCardPreset } from './developer-protocol';
import {
  ELEMENTS_PROTOCOL_TEXT_LIMIT,
  hasExactKeys,
  isBoundedProtocolString as isOpaqueString,
  isRecord,
} from './protocol-validation';
import type {
  ExpressCheckoutBillingDetails,
  ExpressCheckoutElementUpdateOptions,
  ExpressCheckoutElementOptions,
  ExpressCheckoutPaymentMethodPreview,
  ExpressCheckoutShippingAddress,
  ExpressCheckoutShippingRate,
  ExpressCheckoutShippingDetails,
  ExpressCheckoutShippingUpdate,
  ExpressCheckoutWallet,
  PaypercutBillingDetails,
  PaypercutElementsAppearance,
  PaypercutPaymentMethodFutureUsage,
} from '../types/elements';
import {
  CARD_COLLECTION_PROTOCOL_VERSION,
  LEGACY_CARD_COLLECTION_PROTOCOL_VERSION,
  LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION,
  type CardCollectionBootstrap,
} from './card-collection-provider';
import type { CardCollectionConfirmationData } from './card-collection-provider';

export const ELEMENTS_CONTROLLER_CHANNEL = 'paypercut.elements.controller' as const;
export const ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL =
  'paypercut.elements.component.bootstrap' as const;
export const ELEMENTS_COMPONENT_CHANNEL = 'paypercut.elements.component' as const;

export const ElementsControllerMessageType = {
  FrameReady: 'PAYPERCUT_ELEMENTS_CONTROLLER_FRAME_READY',
  Init: 'PAYPERCUT_ELEMENTS_CONTROLLER_INIT',
  Initialized: 'PAYPERCUT_ELEMENTS_CONTROLLER_INITIALIZED',
  Error: 'PAYPERCUT_ELEMENTS_CONTROLLER_ERROR',
} as const;

export const ElementsControllerCommandType = {
  AttachComponent: 'PAYPERCUT_ELEMENTS_CONTROLLER_ATTACH_COMPONENT',
  DetachComponent: 'PAYPERCUT_ELEMENTS_CONTROLLER_DETACH_COMPONENT',
  ValidateComponent: 'PAYPERCUT_ELEMENTS_CONTROLLER_VALIDATE_COMPONENT',
  SubmitComponent: 'PAYPERCUT_ELEMENTS_CONTROLLER_SUBMIT_COMPONENT',
  CardCollectionTokenizationResult:
    'PAYPERCUT_ELEMENTS_CONTROLLER_CARD_COLLECTION_TOKENIZATION_RESULT',
  CancelComponentSubmission: 'PAYPERCUT_ELEMENTS_CONTROLLER_CANCEL_COMPONENT_SUBMISSION',
  AttachActionChannel: 'PAYPERCUT_ELEMENTS_CONTROLLER_ATTACH_ACTION_CHANNEL',
  UpdateOptions: 'PAYPERCUT_ELEMENTS_CONTROLLER_UPDATE_OPTIONS',
  DiscoverDeveloperPresets: 'PAYPERCUT_ELEMENTS_CONTROLLER_DISCOVER_DEVELOPER_PRESETS',
  ApplyDeveloperPreset: 'PAYPERCUT_ELEMENTS_CONTROLLER_APPLY_DEVELOPER_PRESET',
  ApplePayCapabilities: 'PAYPERCUT_ELEMENTS_CONTROLLER_APPLE_PAY_CAPABILITIES',
  ApplePayValidateMerchant: 'PAYPERCUT_ELEMENTS_CONTROLLER_APPLE_PAY_VALIDATE_MERCHANT',
  ApplePayAuthorized: 'PAYPERCUT_ELEMENTS_CONTROLLER_APPLE_PAY_AUTHORIZED',
  ApplePayCanceled: 'PAYPERCUT_ELEMENTS_CONTROLLER_APPLE_PAY_CANCELED',
  GooglePayClick: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_CLICK',
  GooglePayCapabilities: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_CAPABILITIES',
  GooglePayDataChanged: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_DATA_CHANGED',
  GooglePayAuthorized: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_AUTHORIZED',
  GooglePayPaymentData: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_PAYMENT_DATA',
  GooglePayCanceled: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_CANCELED',
  GooglePayError: 'PAYPERCUT_ELEMENTS_CONTROLLER_GOOGLE_PAY_ERROR',
  CardFieldCommand: 'PAYPERCUT_ELEMENTS_CONTROLLER_CARD_FIELD_COMMAND',
  ResolveWalletClick: 'PAYPERCUT_ELEMENTS_CONTROLLER_RESOLVE_WALLET_CLICK',
  ResolveWalletUpdate: 'PAYPERCUT_ELEMENTS_CONTROLLER_RESOLVE_WALLET_UPDATE',
  ResolveWalletConfirmation: 'PAYPERCUT_ELEMENTS_CONTROLLER_RESOLVE_WALLET_CONFIRMATION',
  UpdateComponentOptions: 'PAYPERCUT_ELEMENTS_CONTROLLER_UPDATE_COMPONENT_OPTIONS',
  ComponentEvent: 'PAYPERCUT_ELEMENTS_CONTROLLER_COMPONENT_EVENT',
} as const;

export const ElementsComponentMessageType = {
  FrameReady: 'PAYPERCUT_ELEMENTS_COMPONENT_FRAME_READY',
  Connect: 'PAYPERCUT_ELEMENTS_COMPONENT_CONNECT',
} as const;

export type ElementsControllerOptions = {
  mode: 'payment' | 'setup' | 'subscription';
  amount: number;
  currency: string;
  setupFutureUsage?: PaypercutPaymentMethodFutureUsage;
  locale?: string;
  appearance?: PaypercutElementsAppearance;
};

export type CardFieldComponentType = 'card_number' | 'card_expiry' | 'card_cvc';

export type ElementsComponentType =
  | 'card'
  | 'express_checkout'
  | 'card_collection'
  | CardFieldComponentType;

export type ElementsComponentOptions = Record<string, never> | ExpressCheckoutElementOptions;

export type ElementsControllerInitMessage = {
  channel: typeof ELEMENTS_CONTROLLER_CHANNEL;
  type: typeof ElementsControllerMessageType.Init;
  controllerId: string;
  payload: {
    publishableKey: string;
    apiUrl: string;
    sdkVersion: string;
    options: ElementsControllerOptions;
  };
};

export type ElementsControllerAttachComponentMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.AttachComponent;
  controllerId: string;
  componentId: string;
  componentType: ElementsComponentType;
  componentOptions: ElementsComponentOptions;
  componentGeneration?: number;
};

export type ElementsControllerDetachComponentMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.DetachComponent;
  controllerId: string;
  componentId: string;
  componentType: ElementsComponentType;
};

export type ElementsControllerSubmitComponentMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.SubmitComponent;
  controllerId: string;
  componentId: string;
  componentType: 'card' | 'card_collection';
  requestId: string;
  deadlineAt: number;
  billingDetails?: PaypercutBillingDetails;
};

export type ElementsControllerValidateComponentMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ValidateComponent;
  controllerId: string;
  componentId: string;
  componentType: 'card';
  requestId: string;
};

export type ElementsControllerCardCollectionTokenizationResultMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.CardCollectionTokenizationResult;
  controllerId: string;
  componentId: string;
  componentType: 'card_collection';
  requestId: string;
  result:
    | { status: 'succeeded'; confirmationData: CardCollectionConfirmationData }
    | { status: 'failed'; code: string; message: string; recoverable: boolean };
};

export type ElementsSubmissionCancellationReason =
  | 'canceled'
  | 'timed_out'
  | 'destroyed'
  | 'failed';

export type ElementsControllerCancelComponentSubmissionMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.CancelComponentSubmission;
  controllerId: string;
  componentId: string;
  componentType: ElementsComponentType;
  requestId: string;
  reason: ElementsSubmissionCancellationReason;
};

export type ElementsControllerAttachActionChannelMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.AttachActionChannel;
  controllerId: string;
  componentId: string;
  componentType: ElementsComponentType;
  requestId: string;
  actionId: string;
};

export type ElementsApplePayPaymentRequest = {
  countryCode: string;
  currencyCode: string;
  merchantCapabilities: Array<'supports3DS'>;
  supportedNetworks: Array<'amex' | 'discover' | 'masterCard' | 'visa'>;
  total: { label: string; amount: string; type: 'final' | 'pending' };
  lineItems?: Array<{ label: string; amount: string; type: 'final' | 'pending' }>;
  requiredBillingContactFields?: Array<'name' | 'email' | 'phone' | 'postalAddress'>;
  requiredShippingContactFields?: Array<'name' | 'email' | 'phone' | 'postalAddress'>;
  shippingMethods?: Array<{
    identifier: string;
    label: string;
    detail: string;
    amount: string;
  }>;
};

export type ElementsControllerApplePayCapabilitiesMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ApplePayCapabilities;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  available: boolean;
};

export type ElementsControllerApplePayValidateMerchantMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ApplePayValidateMerchant;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  validationUrl: string;
};

export type ElementsControllerApplePayAuthorizedMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ApplePayAuthorized;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  payment: Record<string, unknown>;
};

export type ElementsControllerApplePayCanceledMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ApplePayCanceled;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
};

export type ElementsControllerGooglePayCapabilitiesMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayCapabilities;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  available: boolean;
};

export type ElementsControllerGooglePayClickMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayClick;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
};

export type ElementsControllerGooglePayDataChangedMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayDataChanged;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  updateId: string;
  dataJson: string;
};

export type ElementsControllerGooglePayAuthorizedMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayAuthorized;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  dataJson: string;
};

export type ElementsControllerGooglePayPaymentDataMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayPaymentData;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  dataJson: string;
};

export type ElementsControllerGooglePayCanceledMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayCanceled;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
};

export type ElementsControllerGooglePayErrorMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.GooglePayError;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  code: string;
  message: string;
};

export type ElementsControllerResolveWalletClickMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ResolveWalletClick;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  accepted: boolean;
  options?: ExpressCheckoutElementUpdateOptions;
  amount: number;
  currency: string;
};

export type ElementsControllerResolveWalletUpdateMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ResolveWalletUpdate;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  updateId: string;
  accepted: boolean;
  update?: ExpressCheckoutShippingUpdate;
  amount: number;
  currency: string;
};

export type ElementsControllerResolveWalletConfirmationMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ResolveWalletConfirmation;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  requestId: string;
  succeeded: boolean;
  error?: { code?: string; message?: string };
};

export type ElementsControllerUpdateOptionsMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.UpdateOptions;
  controllerId: string;
  updateRevision: number;
  options: ElementsControllerOptions;
};

export type ElementsControllerUpdateComponentOptionsMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.UpdateComponentOptions;
  controllerId: string;
  componentId: string;
  componentType: 'express_checkout';
  componentRevision: number;
  componentOptions: ExpressCheckoutElementOptions;
};

export type ElementsControllerCardFieldCommandMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.CardFieldCommand;
  controllerId: string;
  componentId: string;
  componentType: CardFieldComponentType;
  action: 'clear' | 'focus' | 'blur';
};

export type ElementsControllerDiscoverDeveloperPresetsMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.DiscoverDeveloperPresets;
  controllerId: string;
  componentId: string;
  componentType: 'card';
};

export type ElementsControllerApplyDeveloperPresetMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ApplyDeveloperPreset;
  controllerId: string;
  componentId: string;
  componentType: 'card';
  requestId: string;
  presetId: string;
};

export type ElementsComponentConnectMessage = {
  channel: typeof ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL;
  type: typeof ElementsComponentMessageType.Connect;
  controllerId: string;
  componentId: string;
  componentType: ElementsComponentType;
};

export type ElementsComponentEventPayload =
  | {
      /**
       * The component is interactive, not merely provider-initialized. Card
       * emitters must wait until every secure provider frame fills its complete
       * Paypercut-owned field surface and can receive focus and input.
       */
      event: 'ready';
      availablePaymentMethods?: { applePay: boolean; googlePay: boolean };
    }
  | { event: 'secure_field_surface_invalid' }
  | { event: 'resize'; height: number }
  | { event: 'wallet_overlay'; open: boolean }
  | { event: 'change' }
  | {
      event: 'change';
      complete: boolean;
      empty: boolean;
      brand?: string;
      error?: { type: 'incomplete' | 'invalid'; message: string };
    }
  | { event: 'focus' | 'blur' }
  | { event: 'phase'; phase: 'submitting' | 'ready' }
  | {
      event: 'validation_result';
      requestId: string;
      result:
        | { status: 'succeeded' }
        | { status: 'failed'; code: string; message: string; recoverable: boolean };
    }
  | {
      event: 'tokenization_requested';
      requestId: string;
      setupFutureUsage?: PaypercutPaymentMethodFutureUsage;
    }
  | {
      event: 'action_required';
      requestId: string;
      action: { id: string; type: 'three_d_secure' };
    }
  | {
      event: 'submit_result';
      requestId: string;
      result:
        | { status: 'succeeded'; paymentMethod: { id: string; type: string } }
        | { status: 'failed'; code: string; message: string; recoverable: boolean };
    }
  | { event: 'error'; code: string; message: string; recoverable: boolean }
  | { event: 'developer_catalog'; presets: DeveloperCardPreset[] }
  | {
      event: 'developer_apply_result';
      requestId: string;
      presetId: string;
      ok: boolean;
      errorCode?: string;
    }
  | {
      event: 'available_payment_methods_change';
      availablePaymentMethods: { applePay: boolean; googlePay: boolean };
    }
  | { event: 'apple_pay_capabilities_request' }
  | {
      event: 'apple_pay_start';
      requestId: string;
      request: ElementsApplePayPaymentRequest;
    }
  | {
      event: 'apple_pay_merchant_session';
      requestId: string;
      merchantSession: Record<string, unknown>;
    }
  | {
      event: 'google_pay_capabilities_request';
      environment: 'TEST' | 'PRODUCTION';
      requestJson: string;
    }
  | {
      event: 'google_pay_button_target';
      visible: boolean;
      environment: 'TEST' | 'PRODUCTION';
      requestJson: string;
      buttonColor: 'black' | 'white';
      buttonType: string;
      buttonRadius: number;
      target: { x: number; y: number; width: number; height: number };
    }
  | {
      event: 'google_pay_start';
      requestId: string;
      environment: 'TEST' | 'PRODUCTION';
      requestJson: string;
    }
  | {
      event: 'google_pay_data_changed_result';
      requestId: string;
      updateId: string;
      resultJson: string;
    }
  | {
      event: 'google_pay_authorization_result';
      requestId: string;
      resultJson: string;
    }
  | { event: 'wallet_click'; requestId: string; wallet: ExpressCheckoutWallet }
  | {
      event: 'wallet_shipping_address_change';
      requestId: string;
      updateId: string;
      wallet: ExpressCheckoutWallet;
      address: ExpressCheckoutShippingAddress;
    }
  | {
      event: 'wallet_shipping_rate_change';
      requestId: string;
      updateId: string;
      wallet: ExpressCheckoutWallet;
      shippingRate: ExpressCheckoutShippingRate;
    }
  | {
      event: 'wallet_confirm';
      requestId: string;
      wallet: ExpressCheckoutWallet;
      paymentMethod: { id: string; type: 'card' };
      paymentMethodPreview?: ExpressCheckoutPaymentMethodPreview;
      billingDetails?: ExpressCheckoutBillingDetails;
      shippingAddress?: ExpressCheckoutShippingDetails;
      shippingRate?: ExpressCheckoutShippingRate;
    }
  | { event: 'wallet_cancel'; requestId: string; wallet: ExpressCheckoutWallet };

export function isElementsPreviewProgressEvent(
  payload: ElementsComponentEventPayload,
): boolean {
  return (
    payload.event === 'ready' ||
    payload.event === 'resize' ||
    payload.event === 'secure_field_surface_invalid'
  );
}

export type ElementsComponentEventMessage = {
  channel: typeof ELEMENTS_COMPONENT_CHANNEL;
  type: typeof ElementsControllerCommandType.ComponentEvent;
  controllerId: string;
  componentId: string;
  componentType: ElementsComponentType;
  componentGeneration?: number;
  payload: ElementsComponentEventPayload;
};

export type ElementsControllerInitializedMessage = {
  channel: typeof ELEMENTS_CONTROLLER_CHANNEL;
  type: typeof ElementsControllerMessageType.Initialized;
  controllerId: string;
  payload: {
    sessionId: string;
    /** Private provider bootstrap used only by SDK-owned split card fields. */
    cardCollection?: CardCollectionBootstrap;
  };
};

export type ElementsControllerErrorMessage = {
  channel: typeof ELEMENTS_CONTROLLER_CHANNEL;
  type: typeof ElementsControllerMessageType.Error;
  controllerId: string;
  payload: { code: string; message: string; recoverable: boolean };
};

/**
 * Defensive limits for data received from hosted controller/component frames.
 * Names describe the bounded resource so reviewers do not have to reverse-
 * engineer a bare number at each validator call site.
 */
export const CONTROLLER_PROTOCOL_LIMIT = {
  componentHeightCssPixels: 8_192,
  developerPresetCount: 16,
  developerPresetIdentifierCharacters: 64,
  developerPresetLabelCharacters: 80,
  developerPresetDescriptionCharacters: 160,
  cardDescriptorCharacters: 64,
  generalTextCharacters: 256,
  emailCharacters: 320,
  customerNameCharacters: 200,
  googlePayCapabilitiesJsonCharacters: 32_768,
  googlePayRequestJsonCharacters: 65_536,
  googlePayAuthorizationResultJsonCharacters: 16_384,
  googlePayButtonRadiusCssPixels: 1_024,
  googlePayTargetCoordinateMagnitudeCssPixels: 8_192,
  shippingAddressLines: 4,
  countryCodeCharacters: 2,
  currencyCodeCharacters: 3,
  firstCalendarMonth: 1,
  lastCalendarMonth: 12,
  earliestSupportedExpiryYear: 2_000,
  providerPublicKeyCharacters: 512,
} as const;

const AVAILABLE_PAYMENT_METHOD_KEYS = ['applePay', 'googlePay'] as const;
const FRAME_READY_MESSAGE_KEYS = ['channel', 'type'] as const;
const CONTROLLER_INITIALIZED_MESSAGE_KEYS = ['channel', 'type', 'controllerId', 'payload'] as const;
const CONTROLLER_INITIALIZED_PAYLOAD_KEYS = ['sessionId', 'cardCollection'] as const;
const CARD_COLLECTION_BOOTSTRAP_BASE_KEYS = [
  'protocolVersion',
  'driver',
  'publicKey',
  'livemode',
  'expiresAt',
] as const;
const CARD_COLLECTION_LOCALIZED_KEYS = [
  ...CARD_COLLECTION_BOOTSTRAP_BASE_KEYS,
  'locale',
  'messages',
] as const;
const CARD_COLLECTION_SUBMISSION_KEYS = [
  ...CARD_COLLECTION_LOCALIZED_KEYS,
  'capabilities',
] as const;
const CARD_COLLECTION_CAPABILITY_KEYS = ['paymentMethodSubmission'] as const;
const CARD_COLLECTION_UNAVAILABLE_KEYS = [...CARD_COLLECTION_BOOTSTRAP_BASE_KEYS, 'error'] as const;
const CARD_MESSAGES_KEYS = ['card'] as const;
const CARD_MESSAGE_KEYS = [
  'cardNumber',
  'cardNumberPlaceholder',
  'expirationDate',
  'expirationDatePlaceholder',
  'securityCode',
  'cvvPlaceholder',
  'errors',
] as const;
const CARD_ERROR_GROUP_KEYS = ['number', 'expirationDate', 'cvv'] as const;
const CARD_ERROR_KEYS = ['incomplete', 'invalid'] as const;
const CONTROLLER_ERROR_MESSAGE_KEYS = ['channel', 'type', 'controllerId', 'payload'] as const;
const CONTROLLER_ERROR_PAYLOAD_KEYS = ['code', 'message', 'recoverable'] as const;
const COMPONENT_EVENT_MESSAGE_KEYS = [
  'channel',
  'type',
  'controllerId',
  'componentId',
  'componentType',
  'payload',
] as const;
const COMPONENT_EVENT_MESSAGE_KEYS_WITH_GENERATION = [
  ...COMPONENT_EVENT_MESSAGE_KEYS,
  'componentGeneration',
] as const;
const GOOGLE_PAY_BUTTON_TARGET_KEYS = ['x', 'y', 'width', 'height'] as const;

const isAvailablePaymentMethods = (
  value: unknown,
): value is { applePay: boolean; googlePay: boolean } =>
  isRecord(value) &&
  hasExactKeys(value, AVAILABLE_PAYMENT_METHOD_KEYS) &&
  typeof value.applePay === 'boolean' &&
  typeof value.googlePay === 'boolean';

export const isElementsControllerFrameReadyMessage = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, FRAME_READY_MESSAGE_KEYS) &&
  value.channel === ELEMENTS_CONTROLLER_CHANNEL &&
  value.type === ElementsControllerMessageType.FrameReady;

export const isElementsComponentFrameReadyMessage = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, FRAME_READY_MESSAGE_KEYS) &&
  value.channel === ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL &&
  value.type === ElementsComponentMessageType.FrameReady;

export const isElementsControllerInitializedMessage = (
  value: unknown,
): value is ElementsControllerInitializedMessage =>
  isRecord(value) &&
  hasExactKeys(value, CONTROLLER_INITIALIZED_MESSAGE_KEYS) &&
  value.channel === ELEMENTS_CONTROLLER_CHANNEL &&
  value.type === ElementsControllerMessageType.Initialized &&
  isOpaqueString(value.controllerId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
  isRecord(value.payload) &&
  (hasExactKeys(value.payload, ['sessionId'] as const) ||
    hasExactKeys(value.payload, CONTROLLER_INITIALIZED_PAYLOAD_KEYS)) &&
  isOpaqueString(
    value.payload.sessionId,
    ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
  ) &&
  (value.payload.cardCollection === undefined ||
    isCardCollectionBootstrap(value.payload.cardCollection));

const isCardCollectionBootstrap = (value: unknown): value is CardCollectionBootstrap => {
  if (
    !isRecord(value) ||
    value.driver !== 'basis_theory_v2' ||
    !isOpaqueString(value.publicKey, CONTROLLER_PROTOCOL_LIMIT.providerPublicKeyCharacters) ||
    typeof value.livemode !== 'boolean' ||
    typeof value.expiresAt !== 'string' ||
    !Number.isFinite(Date.parse(value.expiresAt))
  ) {
    return false;
  }

  if (value.protocolVersion === LEGACY_CARD_COLLECTION_PROTOCOL_VERSION) {
    return hasExactKeys(value, CARD_COLLECTION_BOOTSTRAP_BASE_KEYS);
  }
  if (
    value.protocolVersion !== LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION &&
    value.protocolVersion !== CARD_COLLECTION_PROTOCOL_VERSION
  ) {
    return false;
  }

  if (
    value.protocolVersion === LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION &&
    hasExactKeys(value, CARD_COLLECTION_LOCALIZED_KEYS)
  ) {
    return (
      isOpaqueString(value.locale, CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters) &&
      isCardCollectionMessages(value.messages)
    );
  }
  if (
    value.protocolVersion === CARD_COLLECTION_PROTOCOL_VERSION &&
    hasExactKeys(value, CARD_COLLECTION_SUBMISSION_KEYS) &&
    isRecord(value.capabilities) &&
    hasExactKeys(value.capabilities, CARD_COLLECTION_CAPABILITY_KEYS)
  ) {
    return (
      value.capabilities.paymentMethodSubmission === true &&
      isOpaqueString(value.locale, CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters) &&
      isCardCollectionMessages(value.messages)
    );
  }
  return (
    hasExactKeys(value, CARD_COLLECTION_UNAVAILABLE_KEYS) && isCardCollectionError(value.error)
  );
};

const isCardCollectionError = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, CONTROLLER_ERROR_PAYLOAD_KEYS) &&
  isOpaqueString(value.code, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
  isOpaqueString(value.message, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters) &&
  typeof value.recoverable === 'boolean';

const isCardCollectionMessages = (value: unknown): boolean => {
  if (!isRecord(value) || !hasExactKeys(value, CARD_MESSAGES_KEYS) || !isRecord(value.card)) {
    return false;
  }
  const card = value.card;
  if (!hasExactKeys(card, CARD_MESSAGE_KEYS) || !isRecord(card.errors)) return false;
  const cardErrors = card.errors;
  if (!hasExactKeys(cardErrors, CARD_ERROR_GROUP_KEYS)) return false;

  const textKeys = CARD_MESSAGE_KEYS.filter((key) => key !== 'errors');
  if (
    !textKeys.every((key) =>
      isOpaqueString(card[key], ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters),
    )
  ) {
    return false;
  }

  return CARD_ERROR_GROUP_KEYS.every((group) => {
    const errors = cardErrors[group];
    return (
      isRecord(errors) &&
      hasExactKeys(errors, CARD_ERROR_KEYS) &&
      CARD_ERROR_KEYS.every((key) =>
        isOpaqueString(errors[key], ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters),
      )
    );
  });
};

export const isElementsControllerErrorMessage = (
  value: unknown,
): value is ElementsControllerErrorMessage =>
  isRecord(value) &&
  hasExactKeys(value, CONTROLLER_ERROR_MESSAGE_KEYS) &&
  value.channel === ELEMENTS_CONTROLLER_CHANNEL &&
  value.type === ElementsControllerMessageType.Error &&
  isOpaqueString(value.controllerId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
  isRecord(value.payload) &&
  hasExactKeys(value.payload, CONTROLLER_ERROR_PAYLOAD_KEYS) &&
  isOpaqueString(value.payload.code, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
  isOpaqueString(value.payload.message, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters) &&
  typeof value.payload.recoverable === 'boolean';

export const isElementsComponentEventMessage = (
  value: unknown,
): value is ElementsComponentEventMessage => {
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      value.componentGeneration === undefined
        ? COMPONENT_EVENT_MESSAGE_KEYS
        : COMPONENT_EVENT_MESSAGE_KEYS_WITH_GENERATION,
    ) ||
    value.channel !== ELEMENTS_COMPONENT_CHANNEL ||
    value.type !== ElementsControllerCommandType.ComponentEvent ||
    !isOpaqueString(value.controllerId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) ||
    !isOpaqueString(value.componentId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) ||
    !isElementsComponentType(value.componentType) ||
    (value.componentGeneration !== undefined &&
      (typeof value.componentGeneration !== 'number' ||
        !Number.isSafeInteger(value.componentGeneration) ||
        value.componentGeneration < 0)) ||
    !isRecord(value.payload)
  ) {
    return false;
  }

  const payload = value.payload;
  if (
    isCardFieldComponentType(value.componentType) &&
    !['ready', 'change', 'focus', 'blur', 'error'].includes(String(payload.event))
  ) {
    return false;
  }
  switch (payload.event) {
    case 'ready':
      if (value.componentType === 'card') return true;
      if (isCardFieldComponentType(value.componentType)) {
        return hasExactKeys(payload, ['event']);
      }
      return isAvailablePaymentMethods(payload.availablePaymentMethods);
    case 'secure_field_surface_invalid':
      return value.componentType === 'card' && hasExactKeys(payload, ['event']);
    case 'change':
      if (value.componentType === 'card') return hasExactKeys(payload, ['event']);
      return (
        isCardFieldComponentType(value.componentType) &&
        isCardFieldChangePayload(payload, value.componentType)
      );
    case 'focus':
    case 'blur':
      return isCardFieldComponentType(value.componentType) && hasExactKeys(payload, ['event']);
    case 'resize':
      return (
        Number.isFinite(payload.height) &&
        (payload.height as number) >= 0 &&
        (payload.height as number) <= CONTROLLER_PROTOCOL_LIMIT.componentHeightCssPixels
      );
    case 'phase':
      return (
        (value.componentType === 'card' || value.componentType === 'card_collection') &&
        ['submitting', 'ready'].includes(String(payload.phase))
      );
    case 'tokenization_requested':
      return (
        value.componentType === 'card_collection' &&
        hasExactKeys(
          payload,
          payload.setupFutureUsage === undefined
            ? (['event', 'requestId'] as const)
            : (['event', 'requestId', 'setupFutureUsage'] as const),
        ) &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        (payload.setupFutureUsage === undefined ||
          payload.setupFutureUsage === 'on_session' ||
          payload.setupFutureUsage === 'off_session')
      );
    case 'validation_result':
      return (
        value.componentType === 'card' &&
        hasExactKeys(payload, ['event', 'requestId', 'result']) &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isValidationResult(payload.result)
      );
    case 'action_required':
      return (
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isRecord(payload.action) &&
        isOpaqueString(
          payload.action.id,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        payload.action.type === 'three_d_secure'
      );
    case 'error':
      return (
        (!isCardFieldComponentType(value.componentType) ||
          hasExactKeys(payload, ['event', 'code', 'message', 'recoverable'])) &&
        isOpaqueString(payload.code, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
        isOpaqueString(payload.message, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters) &&
        typeof payload.recoverable === 'boolean'
      );
    case 'submit_result':
      return (
        (value.componentType === 'card' || value.componentType === 'card_collection') &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isSubmitResult(payload.result)
      );
    case 'developer_catalog':
      return (
        value.componentType === 'card' &&
        Array.isArray(payload.presets) &&
        payload.presets.length > 0 &&
        payload.presets.length <= CONTROLLER_PROTOCOL_LIMIT.developerPresetCount &&
        payload.presets.every(isDeveloperPreset)
      );
    case 'developer_apply_result':
      return (
        value.componentType === 'card' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isOpaqueString(
          payload.presetId,
          CONTROLLER_PROTOCOL_LIMIT.developerPresetIdentifierCharacters,
        ) &&
        typeof payload.ok === 'boolean' &&
        (payload.ok ||
          isOpaqueString(
            payload.errorCode,
            ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
          ))
      );
    case 'available_payment_methods_change':
      return (
        value.componentType === 'express_checkout' &&
        isAvailablePaymentMethods(payload.availablePaymentMethods)
      );
    case 'wallet_overlay':
      return value.componentType === 'express_checkout' && typeof payload.open === 'boolean';
    case 'apple_pay_capabilities_request':
      return value.componentType === 'express_checkout';
    case 'apple_pay_start':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isApplePayPaymentRequest(payload.request)
      );
    case 'apple_pay_merchant_session':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isRecord(payload.merchantSession)
      );
    case 'google_pay_capabilities_request':
      return (
        value.componentType === 'express_checkout' &&
        isGooglePayEnvironment(payload.environment) &&
        isOpaqueJsonString(
          payload.requestJson,
          CONTROLLER_PROTOCOL_LIMIT.googlePayCapabilitiesJsonCharacters,
        )
      );
    case 'google_pay_button_target':
      return (
        value.componentType === 'express_checkout' &&
        typeof payload.visible === 'boolean' &&
        isGooglePayEnvironment(payload.environment) &&
        isOpaqueJsonString(
          payload.requestJson,
          CONTROLLER_PROTOCOL_LIMIT.googlePayRequestJsonCharacters,
        ) &&
        (payload.buttonColor === 'black' || payload.buttonColor === 'white') &&
        isGooglePayButtonType(payload.buttonType) &&
        Number.isFinite(payload.buttonRadius) &&
        (payload.buttonRadius as number) >= 0 &&
        (payload.buttonRadius as number) <=
          CONTROLLER_PROTOCOL_LIMIT.googlePayButtonRadiusCssPixels &&
        isGooglePayButtonTarget(payload.target)
      );
    case 'google_pay_start':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isGooglePayEnvironment(payload.environment) &&
        isOpaqueJsonString(
          payload.requestJson,
          CONTROLLER_PROTOCOL_LIMIT.googlePayRequestJsonCharacters,
        )
      );
    case 'google_pay_data_changed_result':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isOpaqueString(payload.updateId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
        isOpaqueJsonString(
          payload.resultJson,
          CONTROLLER_PROTOCOL_LIMIT.googlePayRequestJsonCharacters,
        )
      );
    case 'google_pay_authorization_result':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isOpaqueJsonString(
          payload.resultJson,
          CONTROLLER_PROTOCOL_LIMIT.googlePayAuthorizationResultJsonCharacters,
        )
      );
    case 'wallet_click':
    case 'wallet_cancel':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isExpressWallet(payload.wallet)
      );
    case 'wallet_shipping_address_change':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isOpaqueString(payload.updateId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
        isExpressWallet(payload.wallet) &&
        isShippingAddress(payload.address)
      );
    case 'wallet_shipping_rate_change':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isOpaqueString(payload.updateId, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
        isExpressWallet(payload.wallet) &&
        isShippingRate(payload.shippingRate)
      );
    case 'wallet_confirm':
      return (
        value.componentType === 'express_checkout' &&
        isOpaqueString(
          payload.requestId,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        isExpressWallet(payload.wallet) &&
        isRecord(payload.paymentMethod) &&
        isOpaqueString(
          payload.paymentMethod.id,
          ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
        ) &&
        payload.paymentMethod.type === 'card' &&
        (payload.paymentMethodPreview === undefined ||
          isPaymentMethodPreview(payload.paymentMethodPreview)) &&
        (payload.billingDetails === undefined || isBillingDetails(payload.billingDetails)) &&
        (payload.shippingAddress === undefined || isShippingDetails(payload.shippingAddress)) &&
        (payload.shippingRate === undefined || isShippingRate(payload.shippingRate))
      );
    default:
      return false;
  }

  function isValidationResult(value: unknown): boolean {
    return (
      isRecord(value) &&
      ((value.status === 'succeeded' && hasExactKeys(value, ['status'])) ||
        (value.status === 'failed' &&
          hasExactKeys(value, ['status', 'code', 'message', 'recoverable']) &&
          isOpaqueString(value.code, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
          isOpaqueString(value.message, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters) &&
          typeof value.recoverable === 'boolean'))
    );
  }
};

const isElementsComponentType = (value: unknown): value is ElementsComponentType =>
  value === 'card' ||
  value === 'express_checkout' ||
  value === 'card_collection' ||
  isCardFieldComponentType(value);

const isCardFieldComponentType = (value: unknown): value is CardFieldComponentType =>
  value === 'card_number' || value === 'card_expiry' || value === 'card_cvc';

const isCardFieldChangePayload = (
  value: Record<string, unknown>,
  componentType: CardFieldComponentType,
): boolean => {
  const allowedKeys = new Set(['event', 'complete', 'empty', 'brand', 'error']);
  if (
    !Object.keys(value).every((key) => allowedKeys.has(key)) ||
    typeof value.complete !== 'boolean' ||
    typeof value.empty !== 'boolean' ||
    (componentType !== 'card_number' && value.brand !== undefined) ||
    (value.brand !== undefined &&
      !isOpaqueString(value.brand, CONTROLLER_PROTOCOL_LIMIT.cardDescriptorCharacters))
  ) {
    return false;
  }
  if (value.error === undefined) return true;
  return (
    isRecord(value.error) &&
    hasExactKeys(value.error, ['type', 'message']) &&
    (value.error.type === 'incomplete' || value.error.type === 'invalid') &&
    isOpaqueString(value.error.message, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters)
  );
};

const isExpressWallet = (value: unknown): value is ExpressCheckoutWallet =>
  value === 'apple_pay' || value === 'google_pay';

const isPaymentMethodPreview = (value: unknown): value is ExpressCheckoutPaymentMethodPreview =>
  isRecord(value) &&
  Object.keys(value).every((key) =>
    ['type', 'brand', 'displayBrand', 'last4', 'expMonth', 'expYear', 'wallet'].includes(key),
  ) &&
  value.type === 'card' &&
  isOpaqueString(value.brand, CONTROLLER_PROTOCOL_LIMIT.cardDescriptorCharacters) &&
  isOpaqueString(value.displayBrand, CONTROLLER_PROTOCOL_LIMIT.cardDescriptorCharacters) &&
  typeof value.last4 === 'string' &&
  /^\d{4}$/.test(value.last4) &&
  (value.expMonth === undefined ||
    (Number.isSafeInteger(value.expMonth) &&
      (value.expMonth as number) >= CONTROLLER_PROTOCOL_LIMIT.firstCalendarMonth &&
      (value.expMonth as number) <= CONTROLLER_PROTOCOL_LIMIT.lastCalendarMonth)) &&
  (value.expYear === undefined ||
    (Number.isSafeInteger(value.expYear) &&
      (value.expYear as number) >= CONTROLLER_PROTOCOL_LIMIT.earliestSupportedExpiryYear)) &&
  isExpressWallet(value.wallet);

const isGooglePayEnvironment = (value: unknown): value is 'TEST' | 'PRODUCTION' =>
  value === 'TEST' || value === 'PRODUCTION';

const isGooglePayButtonType = (value: unknown): value is string =>
  typeof value === 'string' &&
  ['book', 'buy', 'checkout', 'donate', 'order', 'pay', 'plain', 'short', 'subscribe'].includes(
    value,
  );

const isGooglePayButtonTarget = (value: unknown): boolean =>
  isRecord(value) &&
  hasExactKeys(value, GOOGLE_PAY_BUTTON_TARGET_KEYS) &&
  GOOGLE_PAY_BUTTON_TARGET_KEYS.every((key) => Number.isFinite(value[key])) &&
  (value.x as number) >= -CONTROLLER_PROTOCOL_LIMIT.googlePayTargetCoordinateMagnitudeCssPixels &&
  (value.x as number) <= CONTROLLER_PROTOCOL_LIMIT.googlePayTargetCoordinateMagnitudeCssPixels &&
  (value.y as number) >= -CONTROLLER_PROTOCOL_LIMIT.googlePayTargetCoordinateMagnitudeCssPixels &&
  (value.y as number) <= CONTROLLER_PROTOCOL_LIMIT.googlePayTargetCoordinateMagnitudeCssPixels &&
  (value.width as number) > 0 &&
  (value.width as number) <= CONTROLLER_PROTOCOL_LIMIT.componentHeightCssPixels &&
  (value.height as number) > 0 &&
  (value.height as number) <= CONTROLLER_PROTOCOL_LIMIT.componentHeightCssPixels;

const isOpaqueJsonString = (value: unknown, maxLength: number): value is string => {
  if (!isOpaqueString(value, maxLength)) return false;
  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed);
  } catch {
    return false;
  }
};

const isShippingDetails = (value: unknown): value is ExpressCheckoutShippingDetails =>
  isRecord(value) &&
  Object.keys(value).every((key) => key === 'name' || key === 'address') &&
  (value.name === undefined ||
    isOpaqueString(value.name, CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters)) &&
  (value.address === undefined || isAddress(value.address));

const isApplePayPaymentRequest = (value: unknown): value is ElementsApplePayPaymentRequest => {
  if (!isRecord(value) || !isRecord(value.total)) return false;
  return (
    isOpaqueString(value.countryCode, CONTROLLER_PROTOCOL_LIMIT.countryCodeCharacters) &&
    isOpaqueString(value.currencyCode, CONTROLLER_PROTOCOL_LIMIT.currencyCodeCharacters) &&
    Array.isArray(value.merchantCapabilities) &&
    value.merchantCapabilities.every((entry) => entry === 'supports3DS') &&
    Array.isArray(value.supportedNetworks) &&
    value.supportedNetworks.length > 0 &&
    value.supportedNetworks.every((entry) =>
      ['amex', 'discover', 'masterCard', 'visa'].includes(String(entry)),
    ) &&
    isOpaqueString(value.total.label, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
    /^\d+(?:\.\d{1,3})?$/.test(String(value.total.amount)) &&
    ['final', 'pending'].includes(String(value.total.type))
  );
};

const isAddress = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  const allowed = new Set(['name', 'line1', 'line2', 'city', 'state', 'postalCode', 'country']);
  return Object.entries(value).every(
    ([key, entry]) =>
      allowed.has(key) &&
      isOpaqueString(
        entry,
        key === 'country'
          ? CONTROLLER_PROTOCOL_LIMIT.countryCodeCharacters
          : CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters,
      ),
  );
};

const isShippingAddress = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  const allowed = new Set(['recipient', 'addressLine', 'city', 'state', 'postalCode', 'country']);
  return Object.entries(value).every(([key, entry]) => {
    if (!allowed.has(key)) return false;
    if (key === 'addressLine') {
      return (
        Array.isArray(entry) &&
        entry.length <= CONTROLLER_PROTOCOL_LIMIT.shippingAddressLines &&
        entry.every((line) => isOpaqueString(line, CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters))
      );
    }
    return isOpaqueString(
      entry,
      key === 'country'
        ? CONTROLLER_PROTOCOL_LIMIT.countryCodeCharacters
        : CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters,
    );
  });
};

const isShippingRate = (value: unknown): boolean =>
  isRecord(value) &&
  Object.keys(value).every((key) => ['id', 'displayName', 'amount', 'detail'].includes(key)) &&
  isOpaqueString(value.id, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
  isOpaqueString(value.displayName, CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters) &&
  Number.isSafeInteger(value.amount) &&
  (value.amount as number) >= 0;

const isBillingDetails = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  const allowed = new Set(['name', 'email', 'phone', 'address']);
  return Object.entries(value).every(([key, entry]) => {
    if (!allowed.has(key)) return false;
    if (key === 'address') return isAddress(entry);
    return isOpaqueString(
      entry,
      key === 'email'
        ? CONTROLLER_PROTOCOL_LIMIT.emailCharacters
        : CONTROLLER_PROTOCOL_LIMIT.generalTextCharacters,
    );
  });
};

const isSubmitResult = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  if (value.status === 'succeeded') {
    return (
      hasExactKeys(value, ['status', 'paymentMethod']) &&
      isRecord(value.paymentMethod) &&
      hasExactKeys(value.paymentMethod, ['id', 'type']) &&
      isOpaqueString(
        value.paymentMethod.id,
        ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
      ) &&
      isOpaqueString(value.paymentMethod.type, CONTROLLER_PROTOCOL_LIMIT.cardDescriptorCharacters)
    );
  }
  return (
    value.status === 'failed' &&
    hasExactKeys(value, ['status', 'code', 'message', 'recoverable']) &&
    isOpaqueString(value.code, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters) &&
    isOpaqueString(value.message, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters) &&
    typeof value.recoverable === 'boolean'
  );
};

const isDeveloperPreset = (value: unknown): value is DeveloperCardPreset =>
  isRecord(value) &&
  isOpaqueString(value.id, CONTROLLER_PROTOCOL_LIMIT.developerPresetIdentifierCharacters) &&
  isOpaqueString(value.label, CONTROLLER_PROTOCOL_LIMIT.developerPresetLabelCharacters) &&
  isOpaqueString(
    value.description,
    CONTROLLER_PROTOCOL_LIMIT.developerPresetDescriptionCharacters,
  ) &&
  typeof value.last4 === 'string' &&
  /^\d{4}$/.test(value.last4) &&
  ['success', 'challenge', 'warning', 'danger'].includes(String(value.tone));
